<template>
  <PopupMenu :items="menuItems" horizontalPosition="left">
    <template #menuOpener>
      <slot></slot>
    </template>
  </PopupMenu>
</template>

<script setup lang="ts">
import { computed, ComputedRef } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import PopupMenu from '@/components/dialogs/popupmenu/PopupMenu.vue';
import { PopupMenuItem } from '@/components/dialogs/popupmenu/PopupMenuItem';
import { AppName } from '@/data/Branding';
import BrowserDetection from '@/data/BrowserDetection';
import Integrations from '@/data/config/Integrations';
import { setLogoutMarkerInLocalStorage } from '@/data/helpers/TokenHelper';
import IosNative from '@/data/IosNative';
import RouteNames from '@/router/RouteNames';
import { usePushNotificationStore } from '@/stores/PushNotification';
import { useUserStore } from '@/stores/User';

const userStore = useUserStore();
const pushNotificationStore = usePushNotificationStore();

interface Emits {
  (e: 'toggleAppearOfflineClicked'): void,
  (e: 'close'): void,
}
const emit = defineEmits<Emits>();

const menuItems: ComputedRef<PopupMenuItem[]> = computed(() => {
  const items: PopupMenuItem[] = [
    new PopupMenuItem('item', 'Profile settings', {
      optionsType: 'router',
      route: profileSettingsRoute.value,
      elementTitle: 'Open profile settings',
    }),
    new PopupMenuItem('url', 'Privacy', {
      optionsType: 'url',
      url: 'https://cafex.to/privacy/',
      elementTitle: 'See privacy policy',
    }),
  ];
  if (showHelpIcon.value) {
    items.push(
      new PopupMenuItem('item', `${AppName} help`, {
        optionsType: 'base',
        elementId: 'triggerHelp-profile',
        elementTitle: `Get help with ${AppName}`,
      })
    );
  }
  items.push(
    new PopupMenuItem('divider', '', {
      optionsType: 'base',
      cssClass: 'between',
    })
  );
  items.push(
    new PopupMenuItem('item', 'Logout', {
      optionsType: 'callback',
      callback: logoutClicked,
      elementTitle: 'Log out of the system',
    })
  );
  return items;
});

const profileSettingsRoute: ComputedRef<RouteLocationRaw> = computed(() => {
  return { name: RouteNames.PROFILE_SETTINGS_ROUTE_NAME };
});

const showHelpIcon: ComputedRef<boolean> = computed(() => {
  return !!((window as unknown) as { pendo: unknown }).pendo;
});

async function logoutClicked(): Promise<void> {
  try {
    await pushNotificationStore.unregisterPushNotifications();
  } catch (error) {
    // Probably nothing we can do about it
  }

  await userStore.clearUserState();

  // Indicates in the local storage that we are doing a logout. Other Fender tabs are monitoring it
  // and they will clean up states on their side too. This ensures a more consistent behaviour and
  // stops other tabs from automatically re-authenticate via silent refresh.
  setLogoutMarkerInLocalStorage();

  window.location.replace(`${Integrations.BE}/logout`);
  if (BrowserDetection.isIosApp()) {
    IosNative.invokeNative('startNativeLogin', { debugInfo: 'ProfileContextMenu::logoutClicked' });
  }

  emit('close');
}
</script>

<style scoped lang="scss">
.statusEmoji {
  font-size: 1rem;
  padding-right: 0.4rem;
  width: 1.5rem;
  height: 1.5rem;
}

.menuIcon {
  font-size: 1rem;
  padding-right: 0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  color: $colour-icon-primary;
}

.menuText {
  align-self: center;
  color: $colour-text-primary;
  flex: 0 0 auto;
}
</style>
